import { useEffect } from 'react'

import { handleSessionExpired } from '@/lib/auth-session'
import { refreshAccessToken } from '@/lib/http'
import { useAuthStore } from '@/stores/auth.store'

// Refresh this many ms before the access token actually expires, so in-flight
// requests never race the expiry boundary.
const REFRESH_SKEW_MS = 60_000
// setTimeout overflows past ~24.8 days; clamp so a far-future expiry still arms
// a timer (it'll just re-evaluate sooner).
const MAX_TIMEOUT_MS = 2_147_483_647

/**
 * Proactively refreshes the access token shortly before it expires, instead of
 * waiting for a request to fail with 401. Also refreshes when the tab returns to
 * the foreground: mobile/WeChat webviews throttle background timers, so after a
 * long idle the scheduled timer may not have fired — checking on `visibilitychange`
 * covers exactly the "logged in, idle for a long time" case.
 *
 * On refresh failure (refresh token itself expired) it hands off to the shared
 * `handleSessionExpired`, so the UX matches the reactive path.
 */
export const useTokenAutoRefresh = (): void => {
  const accessToken = useAuthStore((state) => state.accessToken)
  const expiresAt = useAuthStore((state) => state.expiresAt)

  useEffect(() => {
    if (!accessToken || !expiresAt) return

    let timer: ReturnType<typeof setTimeout> | null = null

    const runRefresh = async () => {
      const token = await refreshAccessToken()
      // A successful refresh updates the store (accessToken + expiresAt), which
      // re-runs this effect and schedules the next refresh.
      if (!token) handleSessionExpired()
    }

    const delay = expiresAt - REFRESH_SKEW_MS - Date.now()
    if (delay <= 0) {
      void runRefresh()
    } else {
      timer = setTimeout(() => void runRefresh(), Math.min(delay, MAX_TIMEOUT_MS))
    }

    const onVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        Date.now() >= expiresAt - REFRESH_SKEW_MS
      ) {
        void runRefresh()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [accessToken, expiresAt])
}
